import React, { useState, useEffect, useRef } from 'react';
import { Icon } from 'assets/Icon';
import './FAQ.scss';

interface FAQData {
  question: string;
  answer: string;
}

const faqData: FAQData[] = [
  {
    question: "How fast are updates delivered?",
    answer: "SGT Docket Rocket delivers near real-time updates from the Connecticut Judicial Website. You’ll receive quick alerts as soon as new filings, rulings, or schedule changes are posted, much faster and more detailed than the CT Judicial daily email system."
  },
  {
    question: "Can I customize my notifications?",
    answer: "Yes. You can select which Connecticut cases to track and choose the types of updates you want (Plaintiff, Defendant, and Court). Customize alerts via email or push notifications to match your workflow and stay informed without noise."
  },
  {
    question: "What courts does SGT Docket Rocket cover?",
    answer: "SGT Docket Rocket is built specifically for Connecticut state courts, providing direct, streamlined updates from the Connecticut Judicial Branch website."
  },
  {
    question: "What platforms does SGT Docket Rocket support?",
    answer: "SGT Docket Rocket is available on desktop via the web and iOS via a natively built app for fast, on-the-go case tracking."
  },
  {
    question: "How many cases can I track?",
    answer: "You can track unlimited cases. Add as many cases as your practice requires and organize them easily with filters, sorting, and custom notifications."
  },
  {
    question: "Does SGT Docket Rocket support email notifications?",
    answer: "Yes. SGT Docket Rocket sends instant email alerts for all your tracked cases so you stay updated even when you’re away from the app."
  }
];

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick }) => (
  <button 
    className={`faq-item ${isOpen ? 'open' : ''}`} 
    onClick={onClick}
    type="button"
    aria-expanded={isOpen}
  >
    <span>{question}</span>
    <Icon name="plus" className="faq-icon" />
    {isOpen && (
      <div className="faq-answer">
        <p>{answer}</p>
      </div>
    )}
  </button>
);

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const handleClick = (index: number): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const leftColumn = faqData.slice(0, 3);
  const rightColumn = faqData.slice(3, 6);

  return (
    <section className="faq-section">
      <div className={`faq-content ${isVisible ? 'visible' : ''}`} ref={sectionRef}>
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-column">
            {leftColumn.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onClick={() => handleClick(index)}
              />
            ))}
          </div>
          <div className="faq-column">
            {rightColumn.map((item, index) => (
              <FAQItem
                key={index + 3}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index + 3}
                onClick={() => handleClick(index + 3)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className='faq-section-divider'/>
    </section>
  );
};

export default FAQ;

